import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from "axiosConfig";
import moment from 'moment';
import { useOfflineStorage } from './useOfflineStorage';
import { syncOfflineQueue } from './useOfflineQueueSync';
import { setServerFileBlob } from './useOfflineFileBlobs';

type CurrentInfoFileItem = {
  uuid?: string;
  name?: string;
  originalName?: string;
  mimeType?: string;
  type?: string;
  size?: number;
  url?: string;
};

type OrderDetailsFileItem = {
  uuid?: string;
  name?: string;
  originalName?: string;
  mimeType?: string;
  type?: string;
  size?: number;
  url?: string;
};

const isDrawingFileType = (t: string | undefined | null) => {
  if (!t) return false;
  // rysunki z canvas
  return (
    t === 'windowDivisionDrawing' ||
    t === 'windowDivisionDrawingWindowsReplacement'
  );
};

const tryCacheServerFileBlob = async (file: CurrentInfoFileItem) => {
  const uuid = file?.uuid;
  const url = file?.url;
  if (!uuid || !url) return;

  if (!isDrawingFileType(file?.type)) return;

  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return;
    const blob = await res.blob();

    await setServerFileBlob({
      uuid,
      name: file?.name || file?.originalName || 'file',
      type: file?.mimeType || blob.type || 'application/octet-stream',
      blob,
      size: typeof file?.size === 'number' ? file.size : blob.size,
    });
  } catch (e) {
    // best-effort
    console.error('⚠️ Failed to cache server file blob for offline:', uuid, e);
  }
};

const tryCacheServerOrderFileBlob = async (file: OrderDetailsFileItem) => {
  const uuid = file?.uuid;
  const url = file?.url;
  if (!uuid || !url) return;

  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return;
    const blob = await res.blob();

    await setServerFileBlob({
      uuid,
      name: file?.name || file?.originalName || 'file',
      type: file?.mimeType || blob.type || 'application/octet-stream',
      blob,
      size: typeof file?.size === 'number' ? file.size : blob.size,
    });
  } catch (e) {
    // best-effort
    console.error('⚠️ Failed to cache order file blob for offline:', uuid, e);
  }
};

type MeasurementListResponse = {
  measurements?: Array<{ uuid?: string | null }>;
};

export const useOfflineSync = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(() => {
    const stored = localStorage.getItem('lastOfflineSync');
    return stored ? new Date(stored) : null;
  });
  const [todayOrdersSyncedCount, setTodayOrdersSyncedCount] = useState<number>(() => {
    const stored = localStorage.getItem('offlineTodayOrdersCount');
    return stored ? Number(stored) || 0 : 0;
  });
  const [todayOrdersDetails, setTodayOrdersDetails] = useState<any[]>(() => {
    const stored = localStorage.getItem('offlineTodayOrdersDetails');
    return stored ? JSON.parse(stored) : [];
  });
  const [todayOrdersTotalFromCalendar, setTodayOrdersTotalFromCalendar] = useState<number>(() => {
    const stored = localStorage.getItem('offlineTodayOrdersTotalFromCalendar');
    return stored ? Number(stored) || 0 : 0;
  });

  const { saveToCache, getCacheInfo } = useOfflineStorage();

  const syncEndpoints = [
    'users/me',
    'phonePrefixes/label',
  ];

  const measurementConfigEndpoints = [
    'types-of-measurements',
    'rooms',
    'types-of-installation?types=windowsInRawCondition',
    'types-of-blinds-installation',
    'construction-sites?types=windowsInRawCondition',
    'installation-elements?types=windowsInRawCondition',
    'window-addition',
    'types-of-installation?types=windowsReplacement',
    'construction-sites?types=windowsReplacement',
    'installation-elements?types=windowsReplacement',
    'types-of-installation?types=externalDoorsInRawCondition',
    'installation-elements?types=garageDoor',
    'installation-elements?types=rollerBlindsAndBlinds',
    'construction-sites?types=rollerBlindsAndBlinds',
    'raw-condition',
  ];


  const syncOrderDetails = async (orders: any[]) => {
    const detailPromises = orders.map(async (order: any) => {
      try {
        const detailsUrl = `orders/${order.uuid}`;
        const detailsResponse = await axiosInstance.get(detailsUrl, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (detailsResponse.status >= 200 && detailsResponse.status < 300) {
          saveToCache(detailsUrl, detailsResponse.data);

          // Dodatkowo: dociągamy zdjęcia/załączniki z order.files do IndexedDB,
          // żeby były widoczne offline (Android/PWA nie polega na cache HTTP).
          const orderFiles: OrderDetailsFileItem[] =
            (detailsResponse.data as any)?.order?.files || [];
          if (Array.isArray(orderFiles) && orderFiles.length > 0) {
            await Promise.all(orderFiles.map((f) => tryCacheServerOrderFileBlob(f)));
          }
        }

        const detailsNoPhotosUrl = `orders/${order.uuid}?showPhotos=false`;
        const detailsNoPhotosResponse = await axiosInstance.get(detailsNoPhotosUrl, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (detailsNoPhotosResponse.status >= 200 && detailsNoPhotosResponse.status < 300) {
          saveToCache(detailsNoPhotosUrl, detailsNoPhotosResponse.data);
        }

        const detailsWithPhotosUrl = `orders/${order.uuid}?showPhotos=true`;
        const detailsWithPhotosResponse = await axiosInstance.get(detailsWithPhotosUrl, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (detailsWithPhotosResponse.status >= 200 && detailsWithPhotosResponse.status < 300) {
          saveToCache(detailsWithPhotosUrl, detailsWithPhotosResponse.data);

          // W praktyce API może zwracać różne zestawy plików zależnie od showPhotos.
          // Cache'ujemy także z tej odpowiedzi.
          const orderFiles: OrderDetailsFileItem[] =
            (detailsWithPhotosResponse.data as any)?.order?.files || [];
          if (Array.isArray(orderFiles) && orderFiles.length > 0) {
            await Promise.all(orderFiles.map((f) => tryCacheServerOrderFileBlob(f)));
          }
        }

        const measurementsUrl = `measurement/list?orderUuid=${order.uuid}`;
        const measurementsResponse = await axiosInstance.get(measurementsUrl, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (measurementsResponse.status >= 200 && measurementsResponse.status < 300) {
          saveToCache(measurementsUrl, measurementsResponse.data);

          // Dociągamy szczegóły /measurement/current-info dla każdego pomiaru
          // (żeby dało się wejść/wznowić formularze w trybie offline dla istniejących pomiarów).
          const list = measurementsResponse.data as MeasurementListResponse;
          const measurementUuids = Array.isArray(list?.measurements)
            ? list.measurements
                .map((m) => m?.uuid)
                .filter((uuid): uuid is string => typeof uuid === 'string' && uuid.length > 0)
            : [];

          const currentInfoPromises = measurementUuids.map(async (measurementUuid) => {
            try {
              const currentInfoUrl = `measurement/current-info/?measurementUuid=${measurementUuid}`;
              const currentInfoResponse = await axiosInstance.get(currentInfoUrl, {
                headers: { 'Cache-Control': 'no-cache' },
              });

              if (currentInfoResponse.status >= 200 && currentInfoResponse.status < 300) {
                saveToCache(currentInfoUrl, currentInfoResponse.data);

                // Dodatkowo: dociągamy rysunki (file.url) do IndexedDB, żeby były widoczne offline.
                const files: CurrentInfoFileItem[] =
                  (currentInfoResponse.data as any)?.measurement?.measurementInfo?.file || [];
                if (Array.isArray(files) && files.length > 0) {
                  await Promise.all(files.map((f) => tryCacheServerFileBlob(f)));
                }
              }
            } catch (e) {
              // Nie blokujemy synchronizacji całości, jeśli pojedynczy current-info się nie uda.
              console.error(`⚠️ Failed to sync measurement current-info: ${measurementUuid}`, e);
            }
          });

          await Promise.all(currentInfoPromises);
        } else {
        }

        const timeManagementUrl = `activity/time-management?orderUuid=${order.uuid}`;
        const timeManagementResponse = await axiosInstance.get(timeManagementUrl, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (timeManagementResponse.status >= 200 && timeManagementResponse.status < 300) {
          saveToCache(timeManagementUrl, timeManagementResponse.data);
        } else {
          console.error(`⚠️ No time management for order: ${order.uuid}`);
        }

        return true;
      } catch (error) {
        console.error(`❌ Error fetching order data: ${order.uuid}`, error);
        return false;
      }
    });

    const results = await Promise.all(detailPromises);
    const successCount = results.filter(Boolean).length;
    
    return successCount;
  };

  const syncCalendarData = async () => {
    const currentDate = moment().startOf('day');
    const calendarPromises: Promise<boolean>[] = [];
    const todaysOrdersMap: Record<string, any> = {};
    
    
    for (let i = 0; i < 6; i++) {
      const targetDate = moment(currentDate).add(i, 'months');
      const month = targetDate.month() + 1;
      const year = targetDate.year();
      
      
      const url = `orders/date/measurer?month=${month}&year=${year}`;
      const calendarPromise = axiosInstance.get(url, {
        headers: { 'Cache-Control': 'no-cache' },
      }).then(response => {
        if (response.status >= 200 && response.status < 300) {
          saveToCache(url, response.data);

          const todayFormatted = currentDate.format('DD/MM/YYYY');
          if (Array.isArray(response.data?.orders)) {
            response.data.orders.forEach((order: any) => {
              if (order.date === todayFormatted) {
                if (order.uuid && !todaysOrdersMap[order.uuid]) {
                  todaysOrdersMap[order.uuid] = order;
                }
              }
            });
          }
          return true;
        } else {
          console.error(`❌ Failed to sync calendar data for ${month}/${year}:`, response.status);
          return false;
        }
      }).catch(error => {
        console.error(`❌ Error syncing calendar data for ${month}/${year}:`, error);
        return false;
      });
      
      calendarPromises.push(calendarPromise);
    }
    
    const results = await Promise.all(calendarPromises);
    const successCount = results.filter(Boolean).length;
    const todaysOrders = Object.values(todaysOrdersMap);
    setTodayOrdersTotalFromCalendar(todaysOrders.length);
    localStorage.setItem('offlineTodayOrdersTotalFromCalendar', String(todaysOrders.length));

    return { successCount, todaysOrders };
  };

  const syncTodayOrders = async (orders: any[]) => {
    if (!orders || orders.length === 0) {
      setTodayOrdersSyncedCount(0);
      setTodayOrdersDetails([]);
      setTodayOrdersTotalFromCalendar(0);
      localStorage.removeItem('offlineTodayOrdersCount');
      localStorage.removeItem('offlineTodayOrdersDetails');
      localStorage.removeItem('offlineTodayOrdersTotalFromCalendar');
      return 0;
    }

    const normalizedOrders = orders.map((order: any) => ({
      uuid: order.uuid,
    }));

    const fullOrdersData = await Promise.all(
      normalizedOrders.map(async (order: any) => {
        try {
          const detailsResponse = await axiosInstance.get(`orders/${order.uuid}`, {
            headers: { 'Cache-Control': 'no-cache' },
          });

          if (detailsResponse.status >= 200 && detailsResponse.status < 300) {
            return detailsResponse.data?.order || null;
          }
          return null;
        } catch (error) {
          console.error(`❌ Error fetching full order data: ${order.uuid}`, error);
          return null;
        }
      })
    );

    const validFullOrders = fullOrdersData.filter(Boolean);
    const successCount = await syncOrderDetails(normalizedOrders);
    const ordersListResponse = {
      status: "success",
      message: "Order list (offline - today's orders)",
      orders: {
        meta: {
          total: validFullOrders.length,
          perPage: validFullOrders.length,
          currentPage: 1,
          lastPage: 1,
          firstPage: 1,
          firstPageUrl: "/?page=1",
          lastPageUrl: "/?page=1",
          nextPageUrl: null,
          previousPageUrl: null
        },
        data: validFullOrders
      }
    };

    saveToCache('orders?page=1', ordersListResponse);
    saveToCache('orders?status=&page=1', ordersListResponse);
    setTodayOrdersSyncedCount(successCount);
    setTodayOrdersDetails(validFullOrders);
    localStorage.setItem('offlineTodayOrdersCount', String(successCount));
    localStorage.setItem('offlineTodayOrdersDetails', JSON.stringify(validFullOrders));
    localStorage.setItem('offlineTodayOrdersTotalFromCalendar', String(validFullOrders.length));

    return successCount;
  };

  const syncMeasurementConfig = async () => {
    let localSuccessCount = 0;
    let localErrorCount = 0;

    const configPromises = measurementConfigEndpoints.map(async (endpoint) => {
      try {
        const response = await axiosInstance.get(endpoint, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (response.status >= 200 && response.status < 300) {
          saveToCache(endpoint, response.data);
          localSuccessCount++;
        } else {
          localErrorCount++;
        }
      } catch (error) {
        localErrorCount++;
      }
    });

    await Promise.all(configPromises);

    return { success: localSuccessCount, errors: localErrorCount };
  };

  const syncAllData = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error('Brak połączenia z internetem');
      return;
    }

    setIsSyncing(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      toast.info('Rozpoczynam synchronizację danych...');

      const queueResult = await syncOfflineQueue();
      successCount += queueResult.success;
      errorCount += queueResult.failed;

      const endpointPromises = syncEndpoints.map(async (endpoint) => {
        try {
          const response = await axiosInstance.get(endpoint, {
            headers: { 'Cache-Control': 'no-cache' },
          });

          if (response.status >= 200 && response.status < 300) {
            saveToCache(endpoint, response.data);
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      });

      await Promise.all(endpointPromises);

      const measurementConfigResult = await syncMeasurementConfig();
      successCount += measurementConfigResult.success;
      errorCount += measurementConfigResult.errors;

      const { successCount: calendarSyncCount, todaysOrders } = await syncCalendarData();
      successCount += calendarSyncCount;
      if (calendarSyncCount < 6) {
        errorCount += (6 - calendarSyncCount);
      }

      const todayOrdersCount = await syncTodayOrders(todaysOrders);
      successCount += todayOrdersCount;
      console.log(successCount, 'successCount', errorCount, 'errorCount');
      const now = new Date(); 
      setLastSyncTime(now);
      localStorage.setItem('lastOfflineSync', now.toISOString());

      if (errorCount === 0) {
        toast.success(`Synchronizacja zakończona!`);
      } else {
        toast.warning(`⚠️ Synchronizacja zakończona z błędami`);
      }

    } catch (error) {
      console.error('Sync error:', error);
      toast.error('❌ Błąd podczas synchronizacji');
    } finally {
      setIsSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToCache, getCacheInfo]);

  return {
    syncAllData,
    isSyncing,
    lastSyncTime,
    todayOrdersSyncedCount,
    todayOrdersDetails,
    todayOrdersTotalFromCalendar,
  };
};